import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Home, Sparkles, Star } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSeason } from '../../context/SeasonContext';

export const ECOSYSTEM_LANES_TEST_ID = 'ecosystem-lanes-section';

export const ECOSYSTEM_LANE_ROUTES = {
  seeker: '/listings',
  owner: '/register?role=owner',
  tools: '/location-match',
};

export const ECOSYSTEM_LANES_KEYS = [
  'home.ecosystem.badge',
  'home.ecosystem.heading',
  'home.ecosystem.subtitle',
  'home.ecosystem.seeker_title',
  'home.ecosystem.seeker_body',
  'home.ecosystem.seeker_cta',
  'home.ecosystem.owner_title',
  'home.ecosystem.owner_body',
  'home.ecosystem.owner_cta',
  'home.ecosystem.tools_title',
  'home.ecosystem.tools_body',
  'home.ecosystem.tools_cta',
];

export default function EcosystemLanes() {
  const { t } = useLanguage();
  const { theme } = useSeason();

  const lanes = [
    {
      id: 'seeker',
      icon: Home,
      titleKey: 'home.ecosystem.seeker_title',
      titleFallback: 'Find a home',
      bodyKey: 'home.ecosystem.seeker_body',
      bodyFallback: 'Browse relocation-ready listings and compare fit before you inquire.',
      ctaKey: 'home.ecosystem.seeker_cta',
      ctaFallback: 'Browse Homes',
      to: ECOSYSTEM_LANE_ROUTES.seeker,
      accent: 'text-amber-600',
      testId: 'ecosystem-lane-seeker',
    },
    {
      id: 'owner',
      icon: Star,
      titleKey: 'home.ecosystem.owner_title',
      titleFallback: 'List premium housing',
      bodyKey: 'home.ecosystem.owner_body',
      bodyFallback: 'Reach serious movers with trust-first listing presentation.',
      ctaKey: 'home.ecosystem.owner_cta',
      ctaFallback: 'Start Listing',
      to: ECOSYSTEM_LANE_ROUTES.owner,
      accent: 'text-signal-blue',
      testId: 'ecosystem-lane-owner',
    },
    {
      id: 'tools',
      icon: Sparkles,
      titleKey: 'home.ecosystem.tools_title',
      titleFallback: 'Compare & prepare',
      bodyKey: 'home.ecosystem.tools_body',
      bodyFallback: 'Use location match and contract-confidence tools where they actually help.',
      ctaKey: 'home.ecosystem.tools_cta',
      ctaFallback: 'Compare Locations',
      to: ECOSYSTEM_LANE_ROUTES.tools,
      accent: 'text-emerald-600',
      testId: 'ecosystem-lane-tools',
    },
  ];

  return (
    <section
      className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-y border-slate-200"
      data-testid={ECOSYSTEM_LANES_TEST_ID}
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 border"
            style={{
              backgroundColor: `${theme.colors.primary}12`,
              borderColor: `${theme.colors.primary}35`,
            }}
          >
            <Sparkles className="w-4 h-4" style={{ color: theme.colors.primary }} />
            <span style={{ color: theme.colors.primary }} className="text-sm font-medium">
              {t('home.ecosystem.badge', 'One ecosystem, clear lanes')}
            </span>
          </div>

          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-slate-800 mb-3">
            {t('home.ecosystem.heading', 'CHOOSE YOUR RELOCATION LANE')}
          </h2>

          <p className="text-slate-600 max-w-3xl mx-auto text-sm sm:text-base">
            {t(
              'home.ecosystem.subtitle',
              'Seekers, owners, and supporting tools each get a direct path — no dead buttons.',
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {lanes.map((lane) => (
            <div
              key={lane.id}
              className="bg-slate-50 border border-slate-200 rounded-xl p-6 flex flex-col hover:border-slate-300 transition-colors"
            >
              <div
                className={`w-10 h-10 rounded-lg bg-white flex items-center justify-center mb-4 shadow-sm ${lane.accent}`}
              >
                <lane.icon className="w-5 h-5" aria-hidden />
              </div>
              <h3 className="font-heading text-lg font-bold text-slate-800 mb-2">
                {t(lane.titleKey, lane.titleFallback)}
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-5 flex-1">
                {t(lane.bodyKey, lane.bodyFallback)}
              </p>
              <Link
                to={lane.to}
                className="inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                style={{ color: theme.colors.primary }}
                data-testid={lane.testId}
              >
                {t(lane.ctaKey, lane.ctaFallback)}
                <ArrowRight className="w-4 h-4" aria-hidden />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
